/**
 * useCompanies — CR1-FE-03 / CR1-GAP-01
 *
 * TanStack Query hooks for the Companies module.
 * Covers: list, single, properties sub-resource, and CRUD mutations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CompanyListItem } from '@/types/company';
import type { CompanyResponse, Property } from '@shared/schema';
import { apiRequest, apiRequestWithMeta } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';

// ─── Input types ──────────────────────────────────────────────────────────

/**
 * SA-31 CA-03: fiscal fields are now OPTIONAL. `name` is the only
 * required field at the API layer; every other property may be omitted
 * or sent as null/empty string.
 */
export interface CreateCompanyInput {
  name: string;
  legalName?: string | null;
  legalRepresentative?: string | null;
  taxId?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
  slug?: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  addressLine2?: string | null;
  logoUrl?: string | null;
  settings?: Record<string, unknown>;
}

export interface UpdateCompanyInput extends Partial<CreateCompanyInput> {
  isActive?: boolean;
}

// ─── Query keys ───────────────────────────────────────────────────────────

export const COMPANIES_QUERY_KEY = ['/api/companies'] as const;

// ─── Queries ──────────────────────────────────────────────────────────────

export function useCompanies() {
  const { isAuthenticated, hasPermission } = useAuth();

  return useQuery<CompanyListItem[]>({
    queryKey: COMPANIES_QUERY_KEY,
    // EP-07 / SA-00 (ADR 011): permissions-based — PM, BM and SA all have
    // `organization: read`.
    enabled: !!isAuthenticated && hasPermission('organization', 'read'),
    // S3-FE-?? CA-1 fix: `staleTime: 0` + `refetchOnMount: 'always'` forces
    // a fresh fetch on every mount, eliminating the post-login race where
    // a stale-empty response pins the SA selector into
    // `company-selector-empty` until hard refresh. `/api/companies` is
    // cheap (server-cached 5 min) and idempotent, so the extra requests
    // are an acceptable tradeoff for deterministic selector hydration.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCompany(id: string | undefined) {
  return useQuery<CompanyResponse>({
    queryKey: ['/api/companies', id],
    queryFn: () => apiRequest<CompanyResponse>(`/api/companies/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCompanyProperties(companyId: string | undefined) {
  return useQuery<Property[]>({
    queryKey: ['/api/companies', companyId, 'properties'],
    queryFn: () => apiRequest<Property[]>(`/api/companies/${companyId}/properties`),
    enabled: !!companyId,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCompanyInput) =>
      apiRequestWithMeta<CompanyResponse>('/api/companies', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: ({ data: newCompany }) => {
      // Inject new company directly into cache for instant visibility, then sync
      queryClient.setQueryData<CompanyListItem[]>(COMPANIES_QUERY_KEY, (old = []) => [
        ...old,
        { ...newCompany, propertyCount: 0 },
      ]);
      queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyInput }) =>
      apiRequestWithMeta<CompanyResponse>(`/api/companies/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['/api/companies', id] });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ deletedAt: Date }>(`/api/companies/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    },
  });
}

export function useRestoreCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<CompanyResponse>(`/api/companies/${id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    },
  });
}

// Re-export the warning type for ergonomic imports from the form layer.
export type { ApiMetadata, TaxIdFormatWarning } from '@/lib/queryClient';
